<template>
  <f7-popup
    v-model:opened="opened"
    class="pc-select-items-popup"
    close-on-escape
  >
    <f7-page v-if="opened">
      <template #fixed>
        <TheNavbar
          :nav-title="title"
          class="at-navbar secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <UCPreload
        :show="showPreload"
        :number="preloadNum"
      />

      <TransitionGroup>
        <template v-if="!showPreload">
          <!-- List of items -->
          <f7-list class="pc-select-items-popup__list">
            <f7-list-item
              v-for="(item, i) in items"
              :key="i"
              :value="i"
              :title="getListItemTitle(item)"
              :checked="selectedItems.includes(item)"
              name="selected-items"
              :checkbox="maxSelections > 1"
              :radio="maxSelections === 1"
              @change="onChange(item)"
            />
          </f7-list>

          <!-- Apply button -->
          <f7-button
            class="margin-half"
            data-testid="pc-select-items-popup-apply-button"
            fill
            color="green"
            :disabled="selectedItems.length < minSelections || selectedItems.length > maxSelections"
            @click="emitApply"
          >
            {{ applyButtonText || 'Aplicar' }}
          </f7-button>
        </template>
      </TransitionGroup>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check
import { ref, watch } from 'vue';
import TheNavbar from '@/components/TheNavbar.vue';
import UCPreload from './UCPreload.vue';

/** @import { PropType, Ref } from 'vue' */

const props = defineProps({
  /** The title to be displayed in the Navbar of the popup. */
  title: { type: String, default: '' },
  /** The list of items to be displayed in the popup. */
  items: {
    /** @type {PropType<any[] | null>} */
    type: Array,
    default: () => [],
  },
  /** The name of the property in the items array to be used as the title in the list. */
  itemTitleProp: { type: String, default: '' },
  /** The minimum number of items that can be selected. */
  minSelections: { type: Number, default: 0 },
  /** The maximum number of items that can be selected. */
  maxSelections: { type: Number, default: Infinity },
  /** The text to be displayed on the "Apply" button. */
  applyButtonText: { type: String, default: 'Aplicar' },
  /** Flag to control the visibility of the preload. */
  showPreload: { type: Boolean, default: false },
  /** The number of items to be preloaded. */
  preloadNum: { type: Number, default: 3 },
});

const emit = defineEmits(['apply']);

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

/** @type {Ref<any[]>} */
const selectedItems = ref([]);

watch(opened, () => {
  if (!opened.value) {
    selectedItems.value = [];
  }
});

/**
 * Handles the 'change' event when a checkbox is clicked.
 * @param {any} item - The item to be selected or deselected.
 * @returns {void}
 */
function onChange(item) {
  const isSelected = selectedItems.value.includes(item);
  // Checkbox logic
  if (props.maxSelections > 1) {
    if (!isSelected) {
      if (selectedItems.value.length < props.maxSelections) {
        selectedItems.value.push(item);
      }
    } else {
      selectedItems.value = selectedItems.value.filter((selectedItem) => selectedItem !== item);
    }
  }

  // Radio button logic
  if (props.maxSelections === 1) {
    if (!isSelected) {
      selectedItems.value = [item];
    } else {
      selectedItems.value = [];
    }
  }
}

/**
 * Emits the 'apply' event when the 'Apply' button is clicked.
 * @returns {void}
 */
function emitApply() {
  emit('apply', selectedItems.value);
}

/**
 * Get the title of the list item based on the itemTitleProp.
 * @param {any} item - The item from the items array.
 * @returns {string} The title to be displayed in the list.
 */
function getListItemTitle(item) {
  if (typeof item !== 'object') {
    return item.toString();
  }

  // If itemTitleProp is not specified or is an empty string, return the default title
  if (!props.itemTitleProp || props.itemTitleProp.trim() === '') {
    return item.toString();
  }

  // Split the itemTitleProp and get the property value recursively
  const itemProps = props.itemTitleProp.split('.');
  const value = itemProps.reduce((acc, prop) => acc?.[prop], item);

  return value?.toString() ?? '';
}
</script>

<style scoped lang="scss">
.pc-select-items-popup {
  &__list {
    height: calc(100% - 60px);
    overflow-y: scroll;
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}
</style>
